"use client";

import { supabase } from "@/lib/supabase";
import { buildMessageRow, buildProjectMessageRow } from "@/lib/chat-message-rows";
import type { UIMessage } from "ai";
import { useCallback, useRef } from "react";

/**
 * Persists chat messages to Supabase. Call `saveUnsaved` any time you want
 * to flush unsaved messages (after send and after streaming completes).
 *
 * Uses a ref-based saved-ID set to guarantee idempotency — each message is
 * only written once regardless of how many times you call `saveUnsaved`.
 *
 * Returns a Map of { aiSdkMessageId → supabaseMessageId } for the newly
 * inserted rows, so callers can update parts (widgets) after the fact.
 */
export function useMessagePersistence(
  chatIdRef: React.RefObject<string | null>,
  projectChatIdRef?: React.RefObject<string | null>
) {
  const savedIds = useRef(new Set<string>());
  // Next sequence_number to assign on insert. Tracked separately from
  // savedIds.size so it stays correct after deleteFromSequence() removes
  // rows (e.g. on message edit/retry) — savedIds can retain stale ids for
  // messages no longer in the local array without breaking numbering.
  const nextSequenceNumber = useRef(0);

  const saveUnsaved = useCallback(
    async (
      messages: UIMessage[],
      model: string,
      agentId?: string | null
    ): Promise<Map<string, string>> => {
      const projectChatId = projectChatIdRef?.current ?? null;
      const chatId = projectChatId ? null : chatIdRef.current;
      if (!chatId && !projectChatId) return new Map();

      const unsaved = messages.filter((m) => !savedIds.current.has(m.id));
      if (unsaved.length === 0) return new Map();

      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return new Map();

      // The server can now also persist this same turn if it finishes while
      // this client isn't attached (src/app/api/chat/server-persistence.ts).
      // nextSequenceNumber.current only tracks what THIS client has saved —
      // if the server won that race, it's stale (behind the real DB max), so
      // querying fresh here (instead of trusting the local counter) is what
      // keeps both writers assigning the same, correct sequence numbers
      // rather than drifting and silently dropping a later insert under the
      // ignoreDuplicates upsert below.
      const idColumn = projectChatId ? "project_chat_id" : "chat_id";
      const idValue = projectChatId ?? chatId!;
      const { data: lastRow } = await supabase
        .from(projectChatId ? "project_messages" : "messages")
        .select("sequence_number")
        .eq(idColumn, idValue)
        .order("sequence_number", { ascending: false })
        .limit(1)
        .maybeSingle();
      const startSequenceNumber = Math.max(
        nextSequenceNumber.current,
        (lastRow as { sequence_number?: number } | null)?.sequence_number ?? 0
      ) + 1;

      // Branched (not computed as separate `rows`/`table` variables and
      // merged after) so each `.from(...)` call's literal table name stays
      // paired with its own row shape — otherwise TS checks the row union
      // against the from-table union without knowing they correlate
      // positionally, and rejects the (valid) cross terms.
      //
      // upsert + ignoreDuplicates (not insert), conflict target
      // client_message_id (not sequence_number): if the server already saved
      // this exact turn (see saveCompletedMessagesServerSide) while this
      // client wasn't attached, the two writers won't necessarily collide on
      // sequence_number — they can each compute a fresh, non-colliding "max +
      // 1" if they run one after another rather than at the same instant,
      // which would silently duplicate the whole turn under new sequence
      // numbers. client_message_id (the AI SDK message id) is identical
      // across both writers for a given turn, so it's what actually catches
      // "already saved" regardless of timing. See
      // 20260910000002_add_message_client_id_dedup.sql.
      const { error } = projectChatId
        ? await supabase
            .from("project_messages")
            .upsert(
              unsaved.map((m, i) =>
                buildProjectMessageRow(m, {
                  userId: user.id,
                  projectChatId,
                  model,
                  sequenceNumber: startSequenceNumber + i,
                  agentId,
                })
              ),
              { onConflict: "project_chat_id,client_message_id", ignoreDuplicates: true }
            )
        : await supabase
            .from("messages")
            .upsert(
              unsaved.map((m, i) =>
                buildMessageRow(m, {
                  userId: user.id,
                  chatId: chatId!,
                  model,
                  sequenceNumber: startSequenceNumber + i,
                  agentId,
                })
              ),
              { onConflict: "chat_id,client_message_id", ignoreDuplicates: true }
            );

      if (error) {
        console.error("[useMessagePersistence] error:", error.message ?? error, "| code:", error.code, "| details:", error.details);
        return new Map();
      }

      // RETURNING under ignoreDuplicates omits skipped rows, so no .select()
      // above would line up positionally with `unsaved` anyway — correlate
      // by re-fetching on client_message_id, which finds each message's row
      // (whichever writer actually inserted it) regardless of which
      // sequence_number it ended up at.
      const { data: savedRows } = await supabase
        .from(projectChatId ? "project_messages" : "messages")
        .select("id, sequence_number, client_message_id")
        .eq(idColumn, idValue)
        .in("client_message_id", unsaved.map((m) => m.id));

      const mapping = new Map<string, string>();
      let maxSavedSequenceNumber = nextSequenceNumber.current;
      unsaved.forEach((m) => {
        const row = savedRows?.find((r) => r.client_message_id === m.id);
        if (row) {
          savedIds.current.add(m.id);
          mapping.set(m.id, row.id);
          maxSavedSequenceNumber = Math.max(maxSavedSequenceNumber, row.sequence_number);
        }
      });
      nextSequenceNumber.current = maxSavedSequenceNumber;

      return mapping;
    },
    [chatIdRef, projectChatIdRef]
  );

  /** Pre-mark IDs (e.g. messages loaded from DB) so they aren't re-inserted */
  const markSaved = useCallback((ids: string[]) => {
    ids.forEach((id) => savedIds.current.add(id));
    nextSequenceNumber.current += ids.length;
  }, []);

  /** Un-mark an id so its (edited) content is written as a fresh row. */
  const forgetSaved = useCallback((id: string) => {
    savedIds.current.delete(id);
  }, []);

  /**
   * Delete this chat's persisted rows from `sequenceNumber` onward — used
   * when a user message is edited or retried, since the SDK-side resend
   * truncates and replaces messages from that point but has no way to touch
   * the DB itself. Pair with `forgetSaved` for the edited message's own id
   * so its new text gets (re-)written on the next saveUnsaved.
   */
  const deleteFromSequence = useCallback(
    async (sequenceNumber: number) => {
      const projectChatId = projectChatIdRef?.current ?? null;
      const chatId = projectChatId ? null : chatIdRef.current;
      if (!chatId && !projectChatId) return;

      const { error } = projectChatId
        ? await supabase
            .from("project_messages")
            .delete()
            .eq("project_chat_id", projectChatId)
            .gte("sequence_number", sequenceNumber)
        : await supabase
            .from("messages")
            .delete()
            .eq("chat_id", chatId!)
            .gte("sequence_number", sequenceNumber);

      if (error) {
        console.error("[useMessagePersistence] deleteFromSequence error:", error.message ?? error);
        return;
      }

      nextSequenceNumber.current = Math.max(0, sequenceNumber - 1);
    },
    [chatIdRef, projectChatIdRef]
  );

  /** Reset when switching to a new/different chat */
  const reset = useCallback(() => {
    savedIds.current = new Set();
    nextSequenceNumber.current = 0;
  }, []);

  return { saveUnsaved, markSaved, forgetSaved, deleteFromSequence, reset };
}
